import * as z from "zod";

/**
 * A list of dotted paths into a typed "universe" Zod schema. Each path picks
 * one branch of the universe; the framework prunes the universe to just those
 * branches so authors only see (and the runtime only fetches) declared fields.
 *
 * Syntax:
 *   "order.id"                   — object.field
 *   "order.lineItems[].sku"      — descend into array element with []
 *   "order.customer.tags"        — terminate at an array of primitives
 *
 * Overlap rules:
 *   - duplicate paths are idempotent
 *   - if one path is a prefix of another (e.g. "order" + "order.id"), the
 *     shorter path wins: the whole subtree is selected and deeper paths are
 *     redundant
 *   - paths must terminate at a leaf (string/number/etc) OR at an object/array
 *     subtree the author wants in full
 *
 * Errors:
 *   - empty paths, malformed paths, unknown fields, descending into a
 *     non-object/non-array all raise SelectorError. An empty selector array
 *     is valid and produces an empty Input object.
 */
export type Selector = readonly string[];

export class SelectorError extends Error {
  override name = "SelectorError";
  constructor(
    readonly path: string,
    message: string,
  ) {
    super(`Selector path "${path}": ${message}`);
  }
}

type Segment =
  | { readonly kind: "field"; readonly name: string }
  | { readonly kind: "array" };

interface ParsedPath {
  readonly original: string;
  readonly segments: readonly Segment[];
}

const FIELD_NAME_RE = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/;

function parsePath(path: string): readonly Segment[] {
  if (path.length === 0) {
    throw new SelectorError(path, "path is empty");
  }
  const segments: Segment[] = [];
  let buffer = "";
  let expectMore = false;
  const flushField = (): void => {
    if (buffer.length === 0) {
      throw new SelectorError(path, "empty field segment");
    }
    if (!FIELD_NAME_RE.test(buffer)) {
      throw new SelectorError(path, `invalid field name "${buffer}"`);
    }
    segments.push({ kind: "field", name: buffer });
    buffer = "";
  };
  let i = 0;
  while (i < path.length) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const ch = path[i]!;
    if (ch === ".") {
      if (buffer.length > 0) {
        flushField();
      } else if (
        segments.length === 0 ||
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        segments[segments.length - 1]!.kind !== "array"
      ) {
        throw new SelectorError(
          path,
          "unexpected '.' (leading or consecutive dots)",
        );
      }
      expectMore = true;
      i++;
    } else if (ch === "[") {
      if (path[i + 1] !== "]") {
        throw new SelectorError(path, "'[' must be followed by ']'");
      }
      if (buffer.length > 0) {
        flushField();
      } else if (
        segments.length === 0 ||
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        segments[segments.length - 1]!.kind !== "array"
      ) {
        throw new SelectorError(path, "[] without preceding field or array");
      }
      segments.push({ kind: "array" });
      expectMore = false;
      i += 2;
    } else {
      buffer += ch;
      expectMore = false;
      i++;
    }
  }
  if (buffer.length > 0) {
    flushField();
  }
  if (expectMore) {
    throw new SelectorError(path, "trailing '.' with nothing after");
  }
  return segments;
}

interface ZodWrapperDef {
  type: string;
  innerType: z.ZodType;
}

/**
 * Read the internal def off a Zod schema. Zod v4 stores it at `_zod.def`;
 * classic schemas also expose `_def` as a deprecated alias. There is no
 * exported typed accessor — this is the documented escape hatch for tools
 * that need to walk schema structure (mock generators, type generators,
 * pruners like this one). If Zod ever ships a public introspection API,
 * switch to it and drop the casts.
 */
function getDef(schema: z.ZodType): { type: string; [k: string]: unknown } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const internals = (schema as unknown as { _zod?: { def: unknown } })._zod;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const def = internals?.def ?? (schema as unknown as { _def: unknown })._def;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return def as { type: string; [k: string]: unknown };
}

/**
 * Strip wrappers (nullable/optional/default/readonly) off the schema, returning
 * the inner schema plus a function to re-apply the wrappers to a pruned inner.
 */
function unwrap(schema: z.ZodType): {
  inner: z.ZodType;
  rewrap: (pruned: z.ZodType) => z.ZodType;
} {
  const def = getDef(schema) as Partial<ZodWrapperDef> & {
    defaultValue?: unknown;
  };
  if (def.type === "nullable") {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const next = unwrap(def.innerType!);
    return { inner: next.inner, rewrap: (s) => next.rewrap(s).nullable() };
  }
  if (def.type === "optional") {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const next = unwrap(def.innerType!);
    return { inner: next.inner, rewrap: (s) => next.rewrap(s).optional() };
  }
  if (def.type === "readonly") {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const next = unwrap(def.innerType!);
    return { inner: next.inner, rewrap: (s) => next.rewrap(s).readonly() };
  }
  if (def.type === "default") {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const next = unwrap(def.innerType!);
    const defaultValue = def.defaultValue;
    return {
      inner: next.inner,
      // The default value's type isn't statically known at this introspection
      // boundary — `defaultValue` comes from the internal def as `unknown`,
      // and .default() is generic in the schema's output type. The cast
      // bridges those two unknowns; the runtime value is whatever the
      // original schema's .default() received.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      rewrap: (s) => next.rewrap(s).default(defaultValue as never),
    };
  }
  return { inner: schema, rewrap: (s) => s };
}

function pruneSchema(
  schema: z.ZodType,
  paths: readonly ParsedPath[],
): z.ZodType {
  // If any path is empty here, the author asked for this whole subtree.
  if (paths.some((p) => p.segments.length === 0)) {
    return schema;
  }
  const { inner, rewrap } = unwrap(schema);
  const def = getDef(inner);
  if (def.type === "object") {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const shape = def.shape as Record<string, z.ZodType>;
    const groups = new Map<string, ParsedPath[]>();
    for (const path of paths) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const first = path.segments[0]!;
      if (first.kind !== "field") {
        throw new SelectorError(
          path.original,
          "expected a field name but found []",
        );
      }
      let group = groups.get(first.name);
      if (!group) {
        group = [];
        groups.set(first.name, group);
      }
      group.push({ original: path.original, segments: path.segments.slice(1) });
    }
    const newShape: Record<string, z.ZodType> = {};
    for (const [fieldName, restPaths] of groups) {
      if (!(fieldName in shape)) {
        throw new SelectorError(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          restPaths[0]!.original,
          `field "${fieldName}" does not exist in universe`,
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      newShape[fieldName] = pruneSchema(shape[fieldName]!, restPaths);
    }
    let pruned: z.ZodType = z.object(newShape);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const catchall = (def as { catchall?: z.ZodType | undefined }).catchall;
    if (catchall !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      pruned = (pruned as z.ZodObject).catchall(catchall);
    }
    return rewrap(pruned);
  }
  if (def.type === "array") {
    const elementPaths: ParsedPath[] = [];
    for (const path of paths) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const first = path.segments[0]!;
      if (first.kind !== "array") {
        throw new SelectorError(
          path.original,
          "expected [] to descend into array",
        );
      }
      elementPaths.push({
        original: path.original,
        segments: path.segments.slice(1),
      });
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return rewrap(z.array(pruneSchema(def.element as z.ZodType, elementPaths)));
  }
  // Leaf schema with leftover path segments — author is descending into a
  // non-traversable position.
  throw new SelectorError(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    paths[0]!.original,
    `cannot descend into schema of type "${def.type}"`,
  );
}

export function pruneSchemaBySelector(
  universe: z.ZodType,
  selector: Selector,
): z.ZodType {
  // An empty selector is valid: the artifact reads no input fields, so the
  // pruned Input is an empty object. Code that doesn't reference Input
  // type-checks against `Input = {}` and gets `{}` at runtime.
  if (selector.length === 0) {
    return z.object({});
  }
  const parsed: ParsedPath[] = selector.map((path) => ({
    original: path,
    segments: [...parsePath(path)],
  }));
  return pruneSchema(universe, parsed);
}

/**
 * Canonical string for memoization keys — sorts and deduplicates paths so the
 * same logical selection hashes identically regardless of authoring order.
 */
export function canonicalSelectorKey(selector: Selector): string {
  return JSON.stringify([...new Set(selector)].sort());
}

/**
 * The set of top-level field names a selector references in the universe.
 *
 * Callers use this to decide which domain data sources to fetch before
 * building the artifact's input. Because the resolver lives outside the
 * extensions framework (to avoid framework→domain cycles), this helper is
 * how the framework communicates "what does this artifact need" without
 * itself touching any domain code.
 */
export function selectorToTopLevelKinds(selector: Selector): Set<string> {
  const kinds = new Set<string>();
  for (const path of selector) {
    const segments = parsePath(path);
    const first = segments[0];
    if (first?.kind === "field") {
      kinds.add(first.name);
    }
  }
  return kinds;
}

/**
 * Top-level object keys of a contract's universe schema (`order`,
 * `fulfillmentGroup`, `team`, ...). These are the only items the inputSelector
 * UI lets a merchant pick from -- selectors are stored as `string[]` but
 * authoring is constrained to whole top-level subtrees, never dotted leaves.
 * Returns an empty array for non-object universes.
 *
 * Lives next to the other selector primitives so the contract-type RPC, the
 * MCP get-context tool, and any future surfaces that need the same menu all
 * share one source of truth.
 */
export function universeTopLevelKinds(universe: z.ZodType): readonly string[] {
  const { inner } = unwrap(universe);
  const def = getDef(inner);
  if (def.type !== "object") {
    return [];
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const shape = def.shape as Record<string, z.ZodType>;
  return Object.keys(shape);
}
